import { toNonExtractable, toSearchHmacKey } from "@paisa/crypto"
import { create } from "zustand"
import { forgetAllDevices } from "@/lib/device-unlock"
import { queryClient } from "@/lib/query-client"
import { purgeSharePayload } from "@/lib/share-target"

// Queries that stay cached across a lock: the gate itself needs them, and
// none holds vault data (encryption state, profile, household metadata).
const KEEP_ON_LOCK = new Set(["auth-crypto", "profile", "household"])

/**
 * Drop every cached query except the gate's own when the key leaves memory,
 * so decrypted rows, amounts and alert payloads don't sit in the React Query
 * cache behind the veil (and nothing stale renders on the next unlock).
 */
function clearLockedQueryCache() {
  queryClient.removeQueries({
    predicate: (q) => !KEEP_ON_LOCK.has(String(q.queryKey[0])),
  })
}

interface CryptoStore {
  key: CryptoKey | null
  /** Non-extractable HMAC key for person search hashes; null until derived at unlock. */
  hmacKey: CryptoKey | null
  mode: "standard" | "private"
  isUnlocked: boolean
  /** Resolves once the key (non-extractable) is in the store. */
  setKey: (key: CryptoKey) => Promise<void>
  setMode: (mode: "standard" | "private") => void
  clearKey: () => void
}

let unlockAttempt = 0

export const useCryptoStore = create<CryptoStore>((set) => ({
  key: null,
  hmacKey: null,
  mode: "standard",
  isUnlocked: false,
  setKey: (key) => {
    // The extractable DEK never enters the store, so script in the page can't
    // exportKey it (2026-10-02 audit M6). A non-extractable key (device unlock)
    // is stored as is; an extractable one is converted first, together with the
    // HMAC key for person search hashes (the only moment its bytes can be read),
    // and published in one update. Flows that wrap the DEK (recovery phrase,
    // Face ID enrolment) unwrap their own copy from the PIN.
    const attempt = ++unlockAttempt
    if (!key.extractable) {
      set({ key, hmacKey: null, isUnlocked: true })
      return Promise.resolve()
    }
    return Promise.all([toNonExtractable(key), toSearchHmacKey(key)]).then(([locked, hmacKey]) => {
      // A lock (clearKey) or a newer unlock in the meantime wins.
      if (attempt === unlockAttempt) set({ key: locked, hmacKey, isUnlocked: true })
    })
  },
  setMode: (mode) => set({ mode }),
  clearKey: () => {
    unlockAttempt++
    set({ key: null, hmacKey: null, isUnlocked: false })
    clearLockedQueryCache()
  },
}))

// ─── Explicit lock ────────────────────────────────────────────────────────────
// "Lock now" means "ask for my PIN next time", so it also forgets any
// remembered device on this browser. The record is deleted BEFORE the key is
// cleared so the PIN gate can't race it and silently re-unlock. The key is
// cleared either way; if the delete failed, forgetAllDevices has already
// suppressed device unlock for this tab and shown a notice. Returns whether
// the device was forgotten.
//
// A Face ID / Touch ID enrolment (lib/webauthn-unlock.ts) is deliberately NOT
// forgotten here or by the auto-lock: it never unlocks silently — each use
// needs a fresh user-verified WebAuthn ceremony — so surviving a lock is the
// point of it. Logout, PIN change, recovery and turning privacy off clear it.
export async function lockNow(): Promise<boolean> {
  // A staged share may be a statement PDF in plaintext — locking drops it too.
  void purgeSharePayload()
  const forgotten = await forgetAllDevices()
  useCryptoStore.getState().clearKey()
  return forgotten
}

// ─── Inactivity auto-lock (15 min) ────────────────────────────────────────────
export const INACTIVITY_LOCK_MS = 15 * 60 * 1000
/** For copy: "15 min". Derived so the UI can't drift from the timer. */
export const INACTIVITY_LOCK_MINUTES = Math.round(INACTIVITY_LOCK_MS / 60_000)

// Decision: inactivity auto-lock ALSO forgets a remembered device.
//
// The point of the auto-lock is the walked-away-from, still-open tab. If the
// remembered record survived it, the gate (or a simple reload) would silently
// re-unlock and the auto-lock would protect nothing. The timer only runs while
// a tab is open, so "remember" still does its job across reloads, closed tabs
// and restarts — it just doesn't outlive 15 idle minutes in an open tab.
// Flip to false to make auto-lock clear only the in-memory key and let a
// remembered device re-unlock silently.
export const AUTO_LOCK_FORGETS_DEVICE = true

let inactivityTimer: ReturnType<typeof setTimeout> | null = null

export function resetInactivityTimer() {
  if (inactivityTimer) clearTimeout(inactivityTimer)
  inactivityTimer = setTimeout(() => {
    void autoLock()
  }, INACTIVITY_LOCK_MS)
}

async function autoLock(): Promise<boolean> {
  void purgeSharePayload()
  const forgotten = AUTO_LOCK_FORGETS_DEVICE ? await forgetAllDevices() : true
  useCryptoStore.getState().clearKey()
  return forgotten
}

export function startInactivityWatcher() {
  const events = ["click", "keypress", "mousemove", "touchstart"]
  for (const evt of events) {
    document.addEventListener(evt, resetInactivityTimer, { passive: true })
  }
  resetInactivityTimer()
}

export function stopInactivityWatcher() {
  const events = ["click", "keypress", "mousemove", "touchstart"]
  for (const evt of events) {
    document.removeEventListener(evt, resetInactivityTimer)
  }
  if (inactivityTimer) {
    clearTimeout(inactivityTimer)
    inactivityTimer = null
  }
}
